import type { DocFieldSchema } from "@/lib/erp/meta";

/**
 * A FieldGroup is a labeled list of fields.
 * The UI selects a group, and the modal renders exactly `group.fields`.
 */
export type FieldGroup = {
  key: string; // stable-ish id for React keys + selection
  label: string; // section label (clean); used on cards + modal title
  fields: DocFieldSchema[]; // MUST be in the desired display order
};

type MetaGroupInfo = {
  key: string;
  label: string; // section label only
};

export type BuildMetaFieldGroupIndexOptions = {
  defaultTabLabel?: string;
  defaultGroupLabel?: string;
};

/**
 * Build a lookup from `fieldname -> group info` using meta breaks:
 * - Tab Break: changes tab context
 * - Section Break: starts a new group within the tab
 * - Column Break: ignored (layout only)
 *
 * NOTE: This is "structure-only". No visibility/depends_on/hidden filtering here.
 */
export function buildMetaFieldGroupIndex(
  metaFields: DocFieldSchema[],
  {
    defaultTabLabel = "Details",
    defaultGroupLabel = "General",
  }: BuildMetaFieldGroupIndexOptions = {}
): Map<string, MetaGroupInfo> {
  const index = new Map<string, MetaGroupInfo>();

  let tabIndex = 0;
  let sectionIndex = 0;
  let currentTabLabel = defaultTabLabel;
  let currentSectionLabel = defaultGroupLabel;

  for (const f of metaFields) {
    if (!f?.fieldtype) continue;

    if (f.fieldtype === "Tab Break") {
      tabIndex += 1;
      sectionIndex = 0;
      currentTabLabel = f.label || defaultTabLabel;
      currentSectionLabel = defaultGroupLabel;
      continue;
    }

    if (f.fieldtype === "Section Break") {
      sectionIndex += 1;
      currentSectionLabel = f.label || defaultGroupLabel;
      continue;
    }

    if (f.fieldtype === "Column Break") {
      continue;
    }

    const key = `t${tabIndex}-s${sectionIndex}-${currentTabLabel}::${currentSectionLabel}`;
    index.set(f.fieldname, { key, label: currentSectionLabel });
  }

  return index;
}

export type DeriveFieldGroupsFromOrderedFieldsInput = {
  /**
   * Full doctype meta (used ONLY for group labels via breaks).
   */
  metaFields: DocFieldSchema[];
  /**
   * The exact fields (and their order) that the Experience section/widget is showing.
   * Grouping will preserve this order.
   */
  orderedFields: DocFieldSchema[];
  /**
   * Fallback group label when meta mapping doesn't contain a field.
   * This prevents "missing fields" bugs during schema drift.
   */
  defaultGroupLabel?: string;
};

/**
 * Derive FieldGroup[] for a specific Experience section/widget while preserving
 * the Experience ordering:
 *
 * 1) Build meta mapping: fieldname -> group label/key (from Tab/Section breaks)
 * 2) Iterate orderedFields (scope) and assign each field to its group
 *
 * NOTE: Pure grouping only — no hidden/depends_on filtering here.
 */
export function deriveFieldGroupsFromOrderedFields({
  metaFields,
  orderedFields,
  defaultGroupLabel = "General",
}: DeriveFieldGroupsFromOrderedFieldsInput): FieldGroup[] {
  const metaIndex = buildMetaFieldGroupIndex(metaFields, {
    defaultGroupLabel,
  });

  const groupsByKey = new Map<string, FieldGroup>();
  const orderedKeys: string[] = [];

  for (const field of orderedFields) {
    const info = metaIndex.get(field.fieldname);
    const key = info?.key ?? `fallback-${defaultGroupLabel}`;
    const label = info?.label ?? defaultGroupLabel;

    if (!groupsByKey.has(key)) {
      groupsByKey.set(key, { key, label, fields: [] });
      orderedKeys.push(key);
    }

    groupsByKey.get(key)!.fields.push(field);
  }

  return orderedKeys.map((k) => groupsByKey.get(k)!).filter((g) => g.fields.length > 0);
}


